import React, { useState, useEffect } from 'react';
import api from '../api/axiosInstance';
import {
  BookOpen, CheckCircle, ArrowsClockwise, WarningCircle,
  ArrowRight, Bank, CurrencyDollar, Swap
} from '@phosphor-icons/react';
import { Card } from '../components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD',
  minimumFractionDigits: 0, maximumFractionDigits: 0
}).format(v || 0);

const fmtTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  });
};

const TX_TYPE_COLORS = {
  BORROWING:        'bg-red-50 text-red-700 border-red-200',
  INTEREST:         'bg-amber-50 text-amber-700 border-amber-200',
  SETTLEMENT:       'bg-indigo-50 text-indigo-700 border-indigo-200',
  AUTO_RESOLVE:     'bg-emerald-50 text-emerald-700 border-emerald-200',
  FUNDING:          'bg-blue-50 text-blue-700 border-blue-200',
  SIMULATION_EVENT: 'bg-gray-100 text-gray-600 border-gray-300',
  BORROW:           'bg-red-50 text-red-700 border-red-200',
  INJECT:           'bg-emerald-50 text-emerald-700 border-emerald-200',
};

export const DeficitHistoryPage = () => {
  const [history, setHistory] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [txTotal, setTxTotal] = useState(0);
  const [txPage, setTxPage] = useState(1);
  const [txLoading, setTxLoading] = useState(false);
  const [cbInterest, setCbInterest] = useState(0);
  const [mmInterest, setMmInterest] = useState(0);
  const [activeTab, setActiveTab] = useState('transactions'); // 'transactions' | 'deficits'

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, 5000);
    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [histRes, txRes, statsRes] = await Promise.allSettled([
        api.get(`${API}/deficit-history`),
        api.get(`${API}/transactions?page=1&per_page=50`),
        api.get(`${API}/statistics`),
      ]);
      if (histRes.status === 'fulfilled') setHistory(histRes.value.data);
      if (txRes.status === 'fulfilled') {
        setTransactions(txRes.value.data.items || []);
        setTxTotal(txRes.value.data.total || 0);
        setTxPage(1);
      }
      if (statsRes.status === 'fulfilled') {
        setCbInterest(statsRes.value.data.cb_interest_balance || 0);
        setMmInterest(statsRes.value.data.mm_interest_balance || 0);
      }
    } catch (e) { console.error('Fetch data error:', e); }
  };

  const loadMore = async () => {
    if (txLoading) return;
    setTxLoading(true);
    try {
      const nextPage = txPage + 1;
      const res = await api.get(`${API}/transactions?page=${nextPage}&per_page=50`);
      setTransactions(prev => [...prev, ...(res.data.items || [])]);
      setTxPage(nextPage);
    } catch (e) { console.error(e); } finally {
      setTxLoading(false);
    }
  };

  const autoCount = history.filter(h => h.resolution_type === 'AUTO_RESOLVED').length;
  const userCount = history.filter(h => h.resolution_type === 'USER_RESOLVED').length;
  const totalDeficitAmount = history.reduce((s, h) => s + h.deficit_amount, 0);
  const totalInterest = history.reduce((s, h) => s + (h.interest_cost || 0), 0);

  const sourceLabel = (h) => {
    if (h.resolution_type === 'AUTO_RESOLVED') {
      return h.transferred_from?.length > 0
        ? h.transferred_from.map(t => t.channel).join(', ')
        : 'Channel Surplus';
    }
    return h.source === 'CENTRAL_BANK' ? 'Central Bank' : 'Money Market';
  };

  return (
    <div className="space-y-4 fade-in">

      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-black tracking-tight text-gray-900">Transaction Log</h1>
          <p className="text-xs text-gray-500 mt-0.5 uppercase tracking-wide font-medium">
            All fund transfers, borrowings &amp; deficit events
          </p>
        </div>
      </div>

      {/* Interest Account Balances */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="p-3 bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="cb-interest-balance-card">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Bank size={14} weight="duotone" className="text-blue-500" />
            <span className="text-xs text-gray-500 uppercase tracking-wide font-medium">CB Interest</span>
          </div>
          <p className="text-sm font-black font-mono text-blue-700 truncate">{fmt(cbInterest)}</p>
          <p className="text-xs text-gray-400 mt-0.5">Central Bank interest</p>
        </Card>
        <Card className="p-3 bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="mm-interest-balance-card">
          <div className="flex items-center gap-1.5 mb-1.5">
            <CurrencyDollar size={14} weight="duotone" className="text-purple-500" />
            <span className="text-xs text-gray-500 uppercase tracking-wide font-medium">MM Interest</span>
          </div>
          <p className="text-sm font-black font-mono text-purple-700 truncate">{fmt(mmInterest)}</p>
          <p className="text-xs text-gray-400 mt-0.5">Money Market interest</p>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        {[
          { key: 'transactions', label: `Transfer Log (${txTotal})`, icon: <Swap size={13} weight="bold" /> },
          { key: 'deficits',     label: `Deficit Events (${history.length})`,    icon: <WarningCircle size={13} weight="bold" /> },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            data-testid={`tab-${tab.key}`}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
              activeTab === tab.key
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.icon}{tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'transactions' && (
        <Card className="p-0 overflow-hidden bg-white border border-gray-200 rounded-lg" data-testid="transaction-log-table">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
            <div>
              <h2 className="text-xs font-semibold text-gray-900 uppercase tracking-wide">All Fund Transfers</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Showing {transactions.length} of {txTotal} — borrowings, settlements, interest &amp; allocations
              </p>
            </div>
          </div>
          {transactions.length === 0 ? (
            <div className="py-14 text-center">
              <Swap size={32} weight="duotone" className="text-gray-300 mx-auto mb-3" />
              <p className="text-sm font-medium text-gray-400">No transactions yet</p>
              <p className="text-xs text-gray-400 mt-1">Transactions appear after simulation start, borrowing, or stop events</p>
            </div>
          ) : (
            <>
              <div className="divide-y divide-gray-100" data-testid="transactions-table">
                {transactions.map((tx, idx) => (
                  <div key={tx.id || idx} className="px-4 py-3 hover:bg-gray-50 transition-colors" data-testid={`tx-row-${idx}`}>
                    {/* Line 1: type badge + description + amount + time */}
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border flex-shrink-0 ${TX_TYPE_COLORS[tx.type] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                        {tx.type || 'TX'}
                      </span>
                      <span className="text-xs text-gray-600 flex-1 min-w-0 truncate" title={tx.description}>{tx.description || '—'}</span>
                      <span className="text-xs font-black font-mono text-gray-900 flex-shrink-0">{fmt(tx.amount)}</span>
                      <span className="text-xs font-mono text-gray-400 flex-shrink-0 hidden md:inline">{fmtTime(tx.created_at)}</span>
                    </div>
                    {/* Line 2: from → to */}
                    {(tx.from_account || tx.to_account) && (
                      <div className="flex items-center gap-2 mt-1 ml-1">
                        <span className="text-xs font-mono text-gray-500 bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5">{tx.from_account || '—'}</span>
                        <ArrowRight size={11} weight="bold" className="text-gray-400 flex-shrink-0" />
                        <span className="text-xs font-mono text-gray-500 bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5">{tx.to_account || '—'}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {transactions.length < txTotal && (
                <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
                  <span className="text-xs text-gray-400">{transactions.length} of {txTotal} loaded</span>
                  <button
                    onClick={loadMore}
                    disabled={txLoading}
                    data-testid="load-more-transactions"
                    className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 disabled:opacity-50 flex items-center gap-1.5 transition-colors"
                  >
                    {txLoading ? <ArrowsClockwise size={12} className="animate-spin" /> : <ArrowRight size={12} weight="bold" />}
                    {txLoading ? 'Loading…' : `Load next 50`}
                  </button>
                </div>
              )}
            </>
          )}
        </Card>
      )}

      {/* --- DEFICIT EVENTS TAB --- */}
      {activeTab === 'deficits' && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Total Events',   value: history.length,          color: 'text-gray-900',    icon: <BookOpen size={14} weight="duotone" className="text-indigo-500" /> },
              { label: 'Auto-resolved',  value: autoCount,               color: 'text-emerald-600', icon: <ArrowsClockwise size={14} weight="duotone" className="text-emerald-500" /> },
              { label: 'User-approved',  value: userCount,               color: 'text-blue-600',    icon: <CheckCircle size={14} weight="duotone" className="text-blue-500" /> },
              { label: 'Total Deficit',  value: fmt(totalDeficitAmount), color: 'text-gray-900',    icon: <WarningCircle size={14} weight="duotone" className="text-amber-500" />, sub: totalInterest > 0 ? `+${fmt(totalInterest)} interest` : null },
            ].map(({ label, value, color, icon, sub }) => (
              <Card key={label} className="p-3 bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="flex items-center gap-1.5 mb-1.5">{icon}<span className="text-xs text-gray-500 uppercase tracking-wide font-medium">{label}</span></div>
                <p className={`text-sm font-black font-mono truncate ${color}`}>{value}</p>
                {sub && <p className="text-xs text-amber-600 font-mono mt-0.5 truncate">{sub}</p>}
              </Card>
            ))}
          </div>

          <Card className="p-0 overflow-hidden bg-white border border-gray-200 rounded-lg" data-testid="deficit-history-list">
            <div className="px-5 py-3 border-b border-gray-200 bg-gray-50">
              <h2 className="text-xs font-semibold text-gray-900 uppercase tracking-wide">All Deficit Events</h2>
              <p className="text-xs text-gray-500 mt-0.5">Newest first — auto-resolved and user-approved resolutions</p>
            </div>

            {history.length === 0 ? (
              <div className="py-16 text-center">
                <BookOpen size={36} weight="duotone" className="text-gray-300 mx-auto mb-3" />
                <p className="text-sm font-medium text-gray-400">No deficit events recorded yet</p>
                <p className="text-xs text-gray-400 mt-1">Events appear here when liquidity deficits are detected and resolved</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {history.map((item, idx) => (
                  <div key={item.id} className="px-5 py-4 hover:bg-gray-50 transition-colors" data-testid={`deficit-event-${item.id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-mono text-gray-400 w-7 flex-shrink-0">#{history.length - idx}</span>
                        <div>
                          <span className="text-sm font-bold text-gray-900">{item.channel_name}</span>
                          <div className="flex items-center gap-2 mt-1">
                            {item.resolution_type === 'AUTO_RESOLVED' ? (
                              <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5">
                                <ArrowsClockwise size={10} weight="bold" /> Auto-resolved
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2.5 py-0.5">
                                <CheckCircle size={10} weight="bold" /> User-approved
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <span className="text-sm font-black font-mono text-red-600 flex-shrink-0 truncate max-w-[120px]">{fmt(item.deficit_amount)}</span>
                    </div>
                    <div className="flex items-center justify-between ml-10 text-xs">
                      <div className="flex items-center gap-3 text-gray-500">
                        <span className="font-mono">From: <span className="font-semibold text-gray-700">{sourceLabel(item)}</span></span>
                        {item.interest_cost > 0 && (
                          <span className="font-mono text-amber-600">+{fmt(item.interest_cost)} interest</span>
                        )}
                      </div>
                      <span className="font-mono text-gray-400">{fmtTime(item.created_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
};
